import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideDialogConfig } from '@ngneat/dialog';
import { popperVariation, provideTippyConfig, provideTippyLoader, tooltipVariation } from '@ngneat/helipopper/config';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { provideToastr } from 'ngx-toastr';

import { routes } from './app.routes';
import { provideI18n } from './core/i18n';
import { CustomToast } from './shared/components/toast/custom-toast.component';
import { provideMaps } from './shared/data-access/data-access-address';
import { IdleService, provideAuthentication, tokenInterceptor } from './shared/data-access/data-access-authentication';
import { providePayment } from './shared/data-access/data-access-payment';
import { provideToken } from './shared/data-access/data-access-token';
import { loadingInterceptor, provideUi } from './shared/data-access/data-access-ui';
import { provideLottie } from './shared/providers/lottie.provider';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withViewTransitions(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideHttpClient(withInterceptors([tokenInterceptor, loadingInterceptor])),
    provideAnimations(),
    provideToastr({
      toastComponent: CustomToast,
      positionClass: 'toast-bottom-center',
    }),
    provideI18n(),
    provideDialogConfig({
      closeButton: false,
    }),
    provideAuthentication(),
    // Runs before routing: a session left idle across a browser restart is
    // ended here, so the existing authGuard sends the user to /login and no
    // /v1/me request is ever issued for it.
    provideAppInitializer(() => {
      const idleService = inject(IdleService);

      if (idleService.isExpired()) {
        // Deliberately not awaited: bootstrap must not wait on the network. The
        // tokens are cleared before this returns, and the revoke — which still
        // matters, since the idle window is far shorter than the token's own
        // lifetime — finishes while the router redirects.
        idleService.clearExpiredSession();
      }
    }),
    provideLottie(),
    provideUi(),
    provideMaps(),
    provideTippyLoader(() => import('tippy.js')),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        menu: {
          ...popperVariation,
          appendTo: 'parent',
          arrow: false,
          offset: [0, 0],
        },
      },
    }),
    importProvidersFrom(NgxSkeletonLoaderModule.forRoot({ animation: 'pulse' })),
    provideToken(),
    providePayment(),
  ],
};
